import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import type { RentEvidenceResponse, RentEvidenceComp } from '../../types/comps';
import { ExternalLink } from 'lucide-react';

interface RentUpliftEvidenceProps {
  propertyId: string | number;
  /** Current in-place rent per unit (from rent_capex_transparency) */
  currentRentPerUnit?: number;
  /** Market/stabilized rent per unit (from rent_capex_transparency) */
  marketRentPerUnit?: number;
  /** Annual gross rent uplift across all units (from rent_capex_transparency) */
  annualRentUplift?: number;
  /** Total CapEx budget sized from the uplift (from capex_analysis.total_budget) */
  capexBudget?: number;
  /** Current rent source label (e.g. "RentCast validated") */
  currentRentSource?: string;
  /** Market rent source label (e.g. "Rentometer 75th percentile") */
  marketRentSource?: string;
  /** Current rent confidence (e.g. "HIGH") */
  currentRentConfidence?: string;
}

function CompCard({ comp }: { comp: RentEvidenceComp }) {
  const isRenovated = comp.renovation_status === 'renovated';

  return (
    <div className="flex gap-3 items-start py-3 border-b border-stone-700/40 last:border-b-0">
      {comp.street_view_url ? (
        <img
          src={comp.street_view_url}
          alt="Street View"
          className="w-[140px] h-[90px] rounded object-cover bg-stone-700 flex-shrink-0"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : (
        <div className="w-[140px] h-[90px] rounded bg-stone-700 flex-shrink-0 flex items-center justify-center text-stone-400 text-sm">
          No Street View
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-stone-200 text-sm truncate">{comp.address}</div>
        <div className={`text-lg font-bold ${isRenovated ? 'text-emerald-400' : 'text-stone-400'}`}>
          ${comp.monthly_rent.toLocaleString()}/mo
        </div>
        <div className="text-sm text-stone-400">
          {comp.bedrooms ?? '?'}bd/{comp.bathrooms ?? '?'}ba
          {comp.sqft ? ` · ${comp.sqft.toLocaleString()}sf` : ''}
          {` · ${comp.distance_miles.toFixed(2)}mi`}
        </div>
        <div className="text-sm text-stone-400 flex items-center gap-2">
          {comp.rent_per_sqft ? `$${comp.rent_per_sqft.toFixed(2)}/sf` : ''}
          {comp.listing_url && (
            <a
              href={comp.listing_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-400 hover:text-sky-300 inline-flex items-center gap-1"
            >
              View Listing <ExternalLink size={10} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

type VerdictLabel = 'SUPPORTED' | 'MARGINAL' | 'UNSUPPORTED' | 'INCONCLUSIVE';

function VerdictBadge({ verdict }: { verdict: VerdictLabel }) {
  const styles: Record<VerdictLabel, string> = {
    SUPPORTED: 'bg-emerald-400/15 text-emerald-400',
    MARGINAL: 'bg-amber-400/15 text-amber-400',
    UNSUPPORTED: 'bg-red-400/15 text-red-400',
    INCONCLUSIVE: 'bg-stone-500/20 text-stone-400',
  };
  const icons: Record<VerdictLabel, string> = {
    SUPPORTED: '✓',
    MARGINAL: '⚠',
    UNSUPPORTED: '✗',
    INCONCLUSIVE: '~',
  };

  return (
    <span className={`inline-block px-3 py-1 rounded text-sm font-semibold ${styles[verdict]}`}>
      {icons[verdict]} {verdict}
    </span>
  );
}

/** Helper: compute median of a number array */
function medianOf(arr: number[]): number | null {
  if (arr.length === 0) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/** Detect if renovated and unrenovated comps are meaningfully different bedroom counts */
function detectSizeMismatch(
  renovated: RentEvidenceComp[],
  unrenovated: RentEvidenceComp[],
): { mismatch: boolean; renovatedAvgBd: number | null; unrenovatedAvgBd: number | null } {
  const renoBds = renovated.map(c => c.bedrooms).filter((b): b is number => b != null);
  const unrenoBds = unrenovated.map(c => c.bedrooms).filter((b): b is number => b != null);
  if (renoBds.length === 0 || unrenoBds.length === 0) return { mismatch: false, renovatedAvgBd: null, unrenovatedAvgBd: null };
  const renoAvg = renoBds.reduce((a, b) => a + b, 0) / renoBds.length;
  const unrenoAvg = unrenoBds.reduce((a, b) => a + b, 0) / unrenoBds.length;
  return {
    mismatch: Math.abs(renoAvg - unrenoAvg) > 0.5,
    renovatedAvgBd: Math.round(renoAvg * 10) / 10,
    unrenovatedAvgBd: Math.round(unrenoAvg * 10) / 10,
  };
}

export function RentUpliftEvidence({
  propertyId,
  currentRentPerUnit: _currentRentPerUnit,
  marketRentPerUnit: _marketRentPerUnit,
  annualRentUplift: _annualRentUplift,
  capexBudget: _capexBudget,
  currentRentSource: _currentRentSource,
  marketRentSource: _marketRentSource,
  currentRentConfidence: _currentRentConfidence,
}: RentUpliftEvidenceProps) {
  const { data, isLoading, error } = useQuery<RentEvidenceResponse>({
    queryKey: ['rent-evidence', propertyId],
    queryFn: async () => {
      const { data } = await api.get<RentEvidenceResponse>(
        `/comps/rent-evidence/${propertyId}`
      );
      return data;
    },
    enabled: !!propertyId,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <div className="bg-stone-800 border border-stone-700 rounded-lg p-6 animate-pulse">
        <div className="h-5 bg-stone-700 rounded w-48 mb-4" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-48 bg-stone-700 rounded" />
          <div className="h-48 bg-stone-700 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-stone-800 border border-stone-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-sky-400 mb-2">Rent Uplift Evidence</h4>
        <p className="text-sm text-stone-400">
          Comp data unavailable for this property. Check that the property has valid coordinates.
        </p>
      </div>
    );
  }

  if (!data) return null;

  const totalClassified = data.renovated_count + data.unrenovated_count;
  if (totalClassified === 0) {
    return (
      <div className="bg-stone-800 border border-stone-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-sky-400 mb-2">Rent Uplift Evidence</h4>
        <p className="text-sm text-stone-400">
          Insufficient comp data to classify renovated vs unrenovated listings.
          Try expanding the search radius or checking data availability.
        </p>
      </div>
    );
  }

  // Compute $/SF-based verdict when bedroom counts differ (REQ-2, REQ-3)
  const { mismatch: sizeMismatch, renovatedAvgBd, unrenovatedAvgBd } = detectSizeMismatch(
    data.renovated_comps,
    data.unrenovated_comps,
  );

  // $/SF medians for normalized comparison
  const renoPsfs = data.renovated_comps.map(c => c.rent_per_sqft).filter((v): v is number => v != null);
  const unrenoPsfs = data.unrenovated_comps.map(c => c.rent_per_sqft).filter((v): v is number => v != null);
  const renoPsfMedian = medianOf(renoPsfs);
  const unrenoPsfMedian = medianOf(unrenoPsfs);
  const hasPsfData = renoPsfMedian != null && unrenoPsfMedian != null && unrenoPsfMedian > 0;
  const psfPremium = hasPsfData ? (renoPsfMedian! - unrenoPsfMedian!) / unrenoPsfMedian! : null;

  // Decide which verdict label to show
  const resolvedVerdict: VerdictLabel | null = (() => {
    if (!data.verdict) return null;
    if (sizeMismatch) return 'INCONCLUSIVE';
    return data.verdict.verdict;
  })();

  // Narrative story values — prefer props, fall back to comp medians
  // Rent uplift narrative banner removed — consolidated into RentAnalysisConsolidated uplift bar

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-sky-400">Rent Uplift Evidence</h4>

      {/* Rent Uplift Story removed — consolidated into RentAnalysisConsolidated uplift bar above */}

      {/* Side-by-side comp columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Unrenovated */}
        <div className="bg-stone-800 border border-stone-700 rounded-lg p-4">
          <h5 className="text-sm font-semibold text-stone-400 uppercase tracking-wider mb-3">
            Unrenovated Comps
          </h5>
          {data.unrenovated_comps.length > 0 ? (
            data.unrenovated_comps.slice(0, 5).map((comp, i) => (
              <CompCard key={i} comp={comp} />
            ))
          ) : (
            <p className="text-sm text-stone-400 py-4">No unrenovated comps found</p>
          )}
          {data.unrenovated_median != null && (
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-stone-700 text-sm">
              <div>
                <span className="text-stone-400">Median: </span>
                <span className="text-stone-300 font-semibold">${data.unrenovated_median.toLocaleString()}/mo</span>
              </div>
              <div>
                <span className="text-stone-400">{data.unrenovated_count} comps</span>
              </div>
              {data.unrenovated_median_psf != null && (
                <div>
                  <span className="text-stone-400">${data.unrenovated_median_psf.toFixed(2)}/sf</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Renovated */}
        <div className="bg-stone-800 border border-emerald-800/50 rounded-lg p-4">
          <h5 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-3">
            Renovated Comps
          </h5>
          {data.renovated_comps.length > 0 ? (
            data.renovated_comps.slice(0, 5).map((comp, i) => (
              <CompCard key={i} comp={comp} />
            ))
          ) : (
            <p className="text-sm text-stone-400 py-4">No renovated comps found</p>
          )}
          {data.renovated_median != null && (
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-stone-700 text-sm">
              <div>
                <span className="text-stone-400">Median: </span>
                <span className="text-emerald-400 font-semibold">${data.renovated_median.toLocaleString()}/mo</span>
              </div>
              <div>
                <span className="text-stone-400">{data.renovated_count} comps</span>
              </div>
              {data.renovated_median_psf != null && (
                <div>
                  <span className="text-stone-400">${data.renovated_median_psf.toFixed(2)}/sf</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 2-Bedroom Head-to-Head Comparison */}
      {(() => {
        const twobd_renovated = data.renovated_comps.filter(c => c.bedrooms === 2);
        const twobd_unrenovated = data.unrenovated_comps.filter(c => c.bedrooms === 2);

        if (twobd_renovated.length === 0 || twobd_unrenovated.length === 0) return null;

        const median = (arr: number[]): number | null => {
          if (arr.length === 0) return null;
          const sorted = [...arr].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
        };

        const twobd_reno_median = median(twobd_renovated.map(c => c.monthly_rent));
        const twobd_unreno_median = median(twobd_unrenovated.map(c => c.monthly_rent));

        const twobd_reno_psfs = twobd_renovated.filter(c => c.rent_per_sqft).map(c => c.rent_per_sqft!);
        const twobd_unreno_psfs = twobd_unrenovated.filter(c => c.rent_per_sqft).map(c => c.rent_per_sqft!);
        const twobd_reno_psf = median(twobd_reno_psfs);
        const twobd_unreno_psf = median(twobd_unreno_psfs);

        const twobd_premium = (twobd_reno_median != null && twobd_unreno_median != null && twobd_unreno_median > 0)
          ? (twobd_reno_median - twobd_unreno_median) / twobd_unreno_median
          : null;
        const twobd_uplift = (twobd_reno_median != null && twobd_unreno_median != null)
          ? twobd_reno_median - twobd_unreno_median
          : null;

        return (
          <div className="bg-stone-800 border border-stone-700 rounded-lg p-4">
            <h5 className="text-sm font-semibold text-sky-400 uppercase tracking-wider mb-3">
              2-Bedroom Head-to-Head
            </h5>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-sm uppercase tracking-wider text-stone-400 border-b border-stone-700">
                  <th className="text-left py-1.5 font-medium">Metric</th>
                  <th className="text-right py-1.5 font-medium">Unrenovated</th>
                  <th className="text-right py-1.5 font-medium">Renovated</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-stone-700/50">
                  <td className="py-1.5 text-stone-400 text-sm">Median Rent</td>
                  <td className="py-1.5 text-right font-mono text-ivory text-sm">
                    {twobd_unreno_median != null ? `$${twobd_unreno_median.toLocaleString()}/mo` : '--'}
                  </td>
                  <td className="py-1.5 text-right font-mono text-ivory text-sm">
                    {twobd_reno_median != null ? `$${twobd_reno_median.toLocaleString()}/mo` : '--'}
                  </td>
                </tr>
                <tr className="border-b border-stone-700/50">
                  <td className="py-1.5 text-stone-400 text-sm">Sample Size</td>
                  <td className="py-1.5 text-right font-mono text-ivory text-sm">
                    {twobd_unrenovated.length} comp{twobd_unrenovated.length !== 1 ? 's' : ''}
                  </td>
                  <td className="py-1.5 text-right font-mono text-ivory text-sm">
                    {twobd_renovated.length} comp{twobd_renovated.length !== 1 ? 's' : ''}
                  </td>
                </tr>
                {(twobd_unreno_psf != null || twobd_reno_psf != null) && (
                  <tr className="border-b border-stone-700/50">
                    <td className="py-1.5 text-stone-400 text-sm">Median $/SF</td>
                    <td className="py-1.5 text-right font-mono text-ivory text-sm">
                      {twobd_unreno_psf != null ? `$${twobd_unreno_psf.toFixed(2)}/sf` : '--'}
                    </td>
                    <td className="py-1.5 text-right font-mono text-ivory text-sm">
                      {twobd_reno_psf != null ? `$${twobd_reno_psf.toFixed(2)}/sf` : '--'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {twobd_premium != null && twobd_uplift != null && (
              <div className="mt-3 pt-3 border-t border-stone-700">
                <span className="text-emerald-400 font-bold text-sm">
                  +{(twobd_premium * 100).toFixed(1)}%
                </span>
                <span className="text-stone-400 text-sm ml-2">
                  (${Math.round(twobd_uplift).toLocaleString()}/mo uplift)
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Verdict — REQ-2 $/SF normalization, REQ-3 INCONCLUSIVE when sizes differ */}
      {resolvedVerdict && data.verdict && (
        <div className="bg-stone-800 border border-sky-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Observed premium — show raw OR $/SF depending on mismatch */}
              <div>
                <div className="text-sm text-stone-400">
                  {sizeMismatch ? 'Observed ($/SF)' : 'Observed'}
                </div>
                {sizeMismatch && hasPsfData ? (
                  <>
                    <div className="text-lg font-bold text-emerald-400">
                      +{(psfPremium! * 100).toFixed(1)}%
                    </div>
                    <div className="text-sm text-stone-400">
                      ${unrenoPsfMedian!.toFixed(2)}/sf → ${renoPsfMedian!.toFixed(2)}/sf
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-bold text-emerald-400">
                      +{(data.verdict.observed_premium * 100).toFixed(1)}%
                    </div>
                    <div className="text-sm text-stone-400">
                      ${data.unrenovated_median?.toLocaleString()} → ${data.renovated_median?.toLocaleString()}
                    </div>
                  </>
                )}
              </div>
              <div>
                <div className="text-sm text-stone-400">SSOT Assumption</div>
                <div className="text-lg font-bold text-sky-400">
                  +{(data.verdict.assumed_premium * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-stone-400">
                  config.py:RENT_PREMIUM_BY_TIER["{data.tier}"]
                </div>
              </div>
            </div>
            <VerdictBadge verdict={resolvedVerdict} />
          </div>

          {/* Size mismatch explanation (REQ-3) */}
          {sizeMismatch && (
            <div className="mb-3 p-2 bg-amber-500/8 border border-amber-500/25 rounded text-sm text-amber-400">
              Note: Comp sizes differ — renovated avg {renovatedAvgBd}bd vs unrenovated avg {unrenovatedAvgBd}bd.
              Raw rent comparison is misleading; $/SF normalization used instead.
              {hasPsfData && psfPremium != null && (
                <span className="block mt-0.5 text-emerald-400">
                  $/SF premium: +{(psfPremium * 100).toFixed(1)}% (${unrenoPsfMedian!.toFixed(2)}/sf → ${renoPsfMedian!.toFixed(2)}/sf)
                </span>
              )}
            </div>
          )}

          <p className="text-sm text-stone-400">
            {sizeMismatch
              ? 'Comp bedroom counts differ — verdict is inconclusive. $/SF comparison shown above removes the size effect.'
              : data.verdict.explanation}
          </p>
          {data.verdict.warnings.length > 0 && (
            <div className="mt-2 text-sm text-amber-400">
              {data.verdict.warnings.map((w, i) => (
                <div key={i}>⚠ {w}</div>
              ))}
            </div>
          )}
          {data.unknown_count > 0 && (
            <div className="mt-2 text-sm text-stone-400">
              {data.unknown_count} of {data.renovated_count + data.unrenovated_count + data.unknown_count} comps
              classified as "unknown" (excluded from medians).
              Classification uses rent/SF thresholds + keyword signals — not interior photos.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
